import React, {useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Link, Redirect} from 'react-router-dom';
import classNames from 'classnames';
import ClipLoader from 'react-spinners/ClipLoader';
import {useFormatDate, useFormatMessage} from 'hooks';
import Table from 'components/Table';
import {clearUsersData, deleteUser, fetchUsers} from 'state/actions/users';
import paths from 'pages/Router/paths';
import ConfirmationModal from 'components/ConfirmationModal';
import classes from './Users.module.scss';

const Users = () => {
  const dispatch = useDispatch();
  const { usersList, userId, isAdmin, error, loading, deleted } = useSelector(
    state => ({
      userId: state.auth.userData.id,
      usersList: state.users.data,
      isAdmin: state.auth.userData.isAdmin,
      error: state.users.error,
      loading: state.users.loading,
      deleted: state.users.deleted
    }),
    shallowEqual
  );

  const [deleteModal, setDeleteModal] = useState({
    userId: null,
    isOpen: false
  });
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchUsers());
    }

    return () => dispatch(clearUsersData());
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (deleted && !loading) {
      setDeleteModal(prevState => ({
        userId: null,
        isOpen: !prevState.isOpen
      }));
    }
  }, [deleted, loading]);

  useEffect(() => {
     const filteredList = search
    ? usersList.filter(el => el.name.toLowerCase().includes(search.toLowerCase()) || el.email.toLowerCase().includes(search.toLowerCase()))
    : usersList;
     setData(filteredList);
  }, [usersList, search]);

  const redirect = !isAdmin && <Redirect to={paths.CAMPAIGN_LIST} />;

  const onCloseModalHandler = () => {
    setDeleteModal({ userId: null, isOpen: false });
  };

  const onDeleteUserHandler = () => {
    dispatch(deleteUser(deleteModal.userId));
  };

  const columns = [
    {
      Header: useFormatMessage('Users.name'),
      accessor: 'name'
    },
    {
      Header: useFormatMessage('Users.email'),
      accessor: 'email'
    },
    {
      Header: useFormatMessage('Users.admin'),
      accessor: 'isAdmin',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {row.original.isAdmin ? (
            <span className="icon">
              <i className="mdi mdi-check" />
            </span>
          ) : (
            <span className="icon">
              <i className="mdi mdi-close" />
            </span>
          )}
        </small>
      )
    },
    {
      Header: useFormatMessage('Users.allowedCampaigns'),
      accessor: (data) => data.isAdmin ? '∞' : data.allowedCampaigns
    },
    {
      Header: useFormatMessage('Users.startDate'),
      accessor: 'startDate',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.startDate, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </small>
      
      )
    },
    {
      Header: useFormatMessage('Users.endDate'),
      accessor: 'endDate',
      Cell: ({ row }) => (
        <small className="has-text-grey is-abbr-like">
          {useFormatDate(row.original.endDate, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </small>
      )
    },
    {
      Header: '',
      id: 'actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
            <div className="buttons is-right">
              <Link
                to={paths.MODIFY_USER.replace(':id', row.original.id)}
                className="button is-small is-primary"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-account-edit" />
                </span>
              </Link>
              {row.original.id !== userId && (<button
                  type="button"
                  className="button is-small is-danger"
                  onClick={() => setDeleteModal({userId: row.original.id, isOpen: true})}
              >
                <div className="icon is-small">
                  <i className="mdi mdi-trash-can"/>
                </div>
              </button>)}
            </div>
        </>
      ),
      disableSortBy: true
    }
  ];

  const deleteMessage = useFormatMessage('Users.delete');

  const confirmMessage = useFormatMessage('Users.confirm');

  const permDeleteMessage = useFormatMessage('Users.permDelete');

  const cancelMessage = useFormatMessage('Users.cancel');

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <ConfirmationModal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage={deleteMessage}
          title={confirmMessage}
          body={permDeleteMessage}
          cancelButtonMessage={cancelMessage}
          onConfirmation={onDeleteUserHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <section className="hero is-hero-bar">
        <div className="hero-body">
          <div className="level">
            <div className="level-left">
              <div className="level-item">
                <h1 className="title">{useFormatMessage('Users.users')}</h1>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <Link to={paths.ADD_USER} className="button">
                  {useFormatMessage('Users.newUser')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section is-main-section">
        <div className="card has-table has-mobile-sort-spaced">
          <header className="card-header">
            <p className={classNames('card-header-title', classes.tableHeader)}>
              <span>{useFormatMessage('Users.search')}</span>
              <input
                type="text"
                className="input"
                value={search}
                onChange={e => handleSearch(e)}
              />
            </p>
          </header>
          <div className="b-table">
            {loading ? <ClipLoader /> : <Table columns={columns} data={data} />}
            {error && 'Show error'}
          </div>
        </div>
      </section>
    </>
  );
};

export default Users;
